// gatsby-browser.js
import './src/styles/global.css';
import 'swiper/css';
import 'swiper/css/thumbs';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';

import React from 'react';
import { Provider } from 'react-redux';
import { store } from './src/state/store';

import { ReactQueryProvider } from './src/contexts/ReactQueryProvider';
import { initI18n, getInitialLanguage } from './src/i18n';

const LOCALIZED_FONT_ROUTE_REGEX = /^\/(vnd|php)(\/|$)/i;

const syncLocalizedFontClass = (pathname = window.location.pathname) => {
  document.body.classList.toggle(
    'localized-font',
    LOCALIZED_FONT_ROUTE_REGEX.test(pathname),
  );
};

const ZOLT_ORIGIN = 'https://zoltmoney.com';

const redirectNonLandingToZolt = (pathname) => {
  if (typeof window === 'undefined') return false;
  if (process.env.NODE_ENV !== 'production') return false;
  if (!pathname || pathname === '/') return false;
  window.location.replace(
    `${ZOLT_ORIGIN}${pathname}${window.location.search}${window.location.hash}`,
  );
  return true;
};

export const onRouteUpdate = ({ location }) => {
  if (redirectNonLandingToZolt(location?.pathname)) return;
  initI18n(getInitialLanguage(location?.pathname));
  syncLocalizedFontClass(location?.pathname);
};

export const onInitialClientRender = () => {
  syncLocalizedFontClass();
};

async function checkAppVersion() {
  try {
    // Cache-busting query param for Chrome
    const url = `/page-data/app-data.json?_=${Date.now()}`;
    const res = await fetch(url, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    const data = await res.json();
    const buildHash = data.webpackCompilationHash;

    const current = localStorage.getItem('app_build_hash');

    if (current && current !== buildHash) {
      localStorage.setItem('app_build_hash', buildHash);
      window.location.reload(true);
    }

    if (!current) {
      localStorage.setItem('app_build_hash', buildHash);
    }
  } catch (e) {
    console.error('Version check failed', e);
  }
}

export const onClientEntry = () => {
  initI18n(getInitialLanguage(window.location.pathname));

  // Check for app version updates
  checkAppVersion();

  // Unregister any service workers
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
  }
};

export const wrapRootElement = ({ element }) => {
  return (
    <ReactQueryProvider>
      <Provider store={store}>{element}</Provider>
    </ReactQueryProvider>
  );
};
